import React, {useContext, useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {Fade} from "react-reveal";
import "./Greeting.scss";
import premiumHero from "../../assets/lottie/premiumHero";
import DisplayLottie from "../../components/displayLottie/DisplayLottie";
import SocialMedia from "../../components/socialMedia/SocialMedia";
import Button from "../../components/button/Button";
import {illustration, greeting, socialMediaLinks} from "../../portfolio";
import StyleContext from "../../contexts/StyleContext";

export default function Greeting() {
  const {isDark} = useContext(StyleContext);
  const [isResumeOpen, setIsResumeOpen] = useState(false);
  const resumePdfSrc = greeting.resumeLink
    ? `${encodeURI(greeting.resumeLink)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`
    : "";

  useEffect(() => {
    if (!isResumeOpen) return undefined;
    const onEsc = event => {
      if (event.key === "Escape") {
        setIsResumeOpen(false);
      }
    };
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener("keydown", onEsc);
    };
  }, [isResumeOpen]);

  if (!greeting.displayGreeting) {
    return null;
  }
  return (
    <>
      <Fade bottom duration={1000} distance="40px">
        <div className="greet-main" id="greeting">
          <div className="greeting-main">
            <div className="greeting-text-div">
              <div>
                <h1
                  className={
                    isDark ? "dark-mode greeting-text" : "greeting-text"
                  }
                >
                  {greeting.title}
                </h1>
                {greeting.role ? (
                  <p className="greeting-role">{greeting.role}</p>
                ) : null}
                {greeting.lead ? (
                  <h2 className="greeting-lead">{greeting.lead}</h2>
                ) : null}
                <p
                  className={
                    isDark
                      ? "dark-mode greeting-text-p"
                      : "greeting-text-p subTitle"
                  }
                >
                  {greeting.subTitle}
                </p>
                {greeting.proofPills ? (
                  <div
                    className="greeting-proof-pills"
                    aria-label="Portfolio proof points"
                  >
                    {greeting.proofPills.map(item => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                ) : null}
                <div className="button-greeting-div">
                  <Button
                    text="Contact me"
                    href="#contact"
                    analyticsLabel="Hero Contact CTA"
                  />
                  {greeting.resumeLink && (
                    <div>
                      <button
                        type="button"
                        className="main-button resume-preview-trigger"
                        data-analytics-label="View Resume Modal"
                        onClick={() => setIsResumeOpen(true)}
                      >
                        View Resume
                      </button>
                    </div>
                  )}
                </div>
                {greeting.proofHook ? (
                  <div
                    className={
                      isDark
                        ? "greeting-proof-panel dark-mode"
                        : "greeting-proof-panel"
                    }
                  >
                    <p
                      className={
                        isDark
                          ? "dark-mode greeting-proof-line greeting-proof-line--stat"
                          : "greeting-proof-line greeting-proof-line--stat"
                      }
                    >
                      {greeting.proofHook.lead}
                    </p>
                    {greeting.proofHook.confidential ? (
                      <p
                        className={
                          isDark
                            ? "dark-mode greeting-proof-line greeting-proof-line--meta"
                            : "greeting-proof-line greeting-proof-line--meta"
                        }
                      >
                        {greeting.proofHook.confidential}
                      </p>
                    ) : null}
                    <p
                      className={
                        isDark
                          ? "dark-mode greeting-proof-line greeting-proof-line--meta"
                          : "greeting-proof-line greeting-proof-line--meta"
                      }
                    >
                      <span className="greeting-proof-line__prefix">
                        More on{" "}
                      </span>
                      <a
                        href={socialMediaLinks.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="greeting-proof-link"
                        data-analytics-label="Hero GitHub Proof Link"
                      >
                        GitHub
                      </a>
                      <span> · {greeting.proofHook.detailRest}</span>
                    </p>
                  </div>
                ) : null}
                <div id="resume" className="empty-div"></div>
                <SocialMedia />
              </div>
            </div>
            <div className="greeting-image-div">
              {illustration.animated ? (
                <DisplayLottie animationData={premiumHero} />
              ) : (
                <img
                  alt="man sitting on table"
                  src={require("../../assets/images/manOnTable.svg")}
                ></img>
              )}
            </div>
          </div>
        </div>
      </Fade>
      {isResumeOpen && greeting.resumeLink
        ? createPortal(
            <div
              className="resume-modal-overlay"
              onClick={() => setIsResumeOpen(false)}
              role="presentation"
            >
              <div
                className={isDark ? "resume-modal dark-mode" : "resume-modal"}
                onClick={event => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Dmitri Derevjanko resume"
              >
                <div className="resume-modal-header">
                  <div>
                    <span className="resume-modal-eyebrow">Resume Preview</span>
                    <h4>Dmitri Derevjanko CV</h4>
                    <p>AI/ML Solutions Engineer</p>
                  </div>
                  <button
                    type="button"
                    className="resume-modal-close"
                    data-analytics-label="Close Resume Modal"
                    onClick={() => setIsResumeOpen(false)}
                    aria-label="Close resume viewer"
                  >
                    ×
                  </button>
                </div>
                <div className="resume-modal-body">
                  <iframe
                    src={resumePdfSrc}
                    title="Dmitri Derevjanko CV PDF"
                    className="resume-pdf-frame"
                  />
                </div>
                <div className="resume-modal-footer">
                  <a
                    href={greeting.resumeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resume-modal-action resume-modal-action--secondary"
                    data-analytics-label="Resume Open in New Tab"
                  >
                    Open in new tab
                  </a>
                  <a
                    href={greeting.resumeLink}
                    download="Dmitri_Derevjanko_CV.pdf"
                    className="resume-modal-action"
                    data-analytics-label="Resume Download PDF"
                  >
                    Download PDF
                  </a>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
